import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, isSameMonth } from 'date-fns';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styles from './styles';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import DayWeekPicker from 'components/DayWeekPicker';
import { Option, isIOS } from 'components/Option';
import MonthPicker from 'components/MonthPicker';
import { DRAWER_TYPES, SCHEDULE_DAY_VIEWS, SCHEDULE_DAY_VIEWS_OPTIONS, SCHEDULE_VIEW_TYPES, SCHEDULE_VIEW_TYPES_OPTIONS } from 'pages/ScheduleV6/constants/constants';
import SearchForm from 'components/SearchForm/SearchForm';
import schedulePageActions from 'store/schedule/schedulePageActions';
import { hasPermissionsFor } from 'helpers/_helpers';
import { useScheduleActions } from '../hooks/useScheduleActions';
import FilterIcon from 'assets/images/filter-icon.svg'
import PrintIcon from 'assets/images/print-icon.svg';
import AddIcon from 'assets/images/add-in-square.svg';
import CustomMenu from 'components/CustomMenu/CustomMenu';
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";


const ScheduleV6Header = ({
  classes,
  openDrawer,
  selectedDate,
  updateSelectedDate,
  showPublishModal,
  showUpdateModal,
  canPublish = false,
  canUpdate = false,
  showReconcileModal,
  updateSetWorklogsControl,
  hasEditAccess = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const scheduleView = useSelector((state) => state.schedule.scheduleView);
  const scheduleDayView = useSelector((state) => state.schedule.scheduleDayView);
  const showAllLocations = useSelector((state) => state.schedule.showAllLocations);

  const { hasUnreconciledSheets } = useScheduleActions();
  const hasSchedulePermission = hasPermissionsFor('scheduleFullAccess');
  const displayAddNote = hasPermissionsFor('notesRead') || hasPermissionsFor('notesFullAccess');

  const isToday = () => {
    if (!selectedDate) return true;
    return isSameMonth(new Date(), new Date(selectedDate));
  };

  const onDateSelect = (date) => {
    if (date) {
      const segments = location.pathname.split('/').filter(Boolean);
      if (!segments.includes('schedule')) {
        segments.push('schedule');
      }
      const dateSegment = format(date, 'yyyy-MM-dd');
      if (/\d{4}-\d{2}-\d{2}$/.test(location.pathname)) {
        segments[segments.length - 1] = dateSegment;
      } else {
        segments.push(dateSegment);
      }
      navigate(`/${segments.join('/')}${location.search}`);
    }
  };

  const setToday = () => onDateSelect(new Date());

  const handleViewTypeChange = (e) => {
    e.preventDefault();
    const type = e?.target?.value;
    if (!type || !SCHEDULE_VIEW_TYPES[type]) throw new Error('Bad schedule type');
    dispatch(schedulePageActions.setScheduleView(type));
    if (type === SCHEDULE_VIEW_TYPES.monthly) dispatch(schedulePageActions.setSheets([]));
  }
  const handleDayViewChange = (e) => {
    e.preventDefault();
    const type = e?.target?.value;
    if (!type || !SCHEDULE_DAY_VIEWS[type]) throw new Error('Bad day view');

    dispatch(schedulePageActions.setScheduleDayView(type));
  }

  const addOptions = useMemo(() => {
    const options = [{
      value: 'addSheet',
      onClick: openDrawer('addWorklog'),
      label: (
        <span className={classes.addOption}>
          + Add Worklog
        </span>
      )
    }]

    if (displayAddNote) {
      options.push({
        value: 'addNote',
        onClick: openDrawer('addNote'),
        label: (
          <span className={classes.addOption}>
            + Add Note
          </span>
        )
      })
    }

    return options;
  }, [openDrawer, displayAddNote])

  const subtitleText = useMemo(() => {
    if (scheduleView === SCHEDULE_VIEW_TYPES.daily) {
      return SCHEDULE_DAY_VIEWS_OPTIONS[scheduleDayView]
    }
    return `${SCHEDULE_VIEW_TYPES_OPTIONS[scheduleView]} View`;
  }, [scheduleDayView, scheduleView]);


  const onSearch = (value) => {
    dispatch(schedulePageActions.setSearchValue(value));
  }

  return (
    <header className={classes.header}>
      <Typography variant="h3">
        Schedule
        <span className={classes.subtitle}>{subtitleText}</span>
      </Typography>

      {showAllLocations ? (
        <>
          <span style={{ width: 210 }} />
        </>
      ) : (
        <div className={classes.publishGroup}>
          {hasSchedulePermission
            ? (
              <>
                <Button
                  id="manageButtons"
                  variant="contained"
                  color="secondary"
                  onClick={showPublishModal}
                  disableTouchRipple
                  disabled={!hasEditAccess || !canPublish}
                >
                  Publish
                </Button>
                <Button
                  id="manageButtons"
                  variant="contained"
                  color="secondary"
                  className={classnames(canUpdate ? classes.red : null)}
                  onClick={showUpdateModal}
                  disableTouchRipple
                  disabled={!hasEditAccess || !canUpdate}
                >
                  Update
                </Button>
              </>
            ) : null
          }
          {(hasUnreconciledSheets && hasSchedulePermission) && (
            <Button
              variant="contained"
              color="secondary"
              className={classes.red}
              onClick={showReconcileModal}
              disabled={!hasEditAccess}
              disableTouchRipple
            >
              Reconcile
            </Button>
          )}
        </div>
      )}



      <div className={classnames(classes.iconButtonGroup)}>
        {!!hasEditAccess && (
          <CustomMenu MenuProps={{ className: classes.addButton }} options={addOptions} >
            {({ handleButtonClick, open }) => (
              <Button
                variant="text"
                className={classes.iconButton}
                onClick={handleButtonClick}
                disableRipple
                endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                disabled={!hasEditAccess}
              >
                <img src={AddIcon} alt="add" />
              </Button>
            )}
          </CustomMenu>
        )}
        <Button
          variant="text"
          className={classes.iconButton}
          onClick={updateSetWorklogsControl(true)}
          disableRipple
        >
          <img src={PrintIcon} alt="print" />
        </Button>
        <Button
          variant="text"
          className={classes.iconButton}
          onClick={openDrawer(DRAWER_TYPES.filters)}
          disableTouchRipple
        >
          <img src={FilterIcon} alt="filter" />
        </Button>
      </div>

      <div className={classnames(classes.group, classes.searchGroup)} >
        <SearchForm isDynamicSearch={true} search={onSearch} />
      </div>

      <div className={classes.group}>
          {scheduleView === SCHEDULE_VIEW_TYPES.daily ? (
            <TextField
              select
              id="dayType"
              name="dayType" 
              value={scheduleDayView}
              onChange={handleDayViewChange}
              className={classnames(classes.selector, classes.selectorWide)}
              variant="outlined"
              SelectProps={{
                native: isIOS,
              }}
            >
              {Object.entries(SCHEDULE_DAY_VIEWS_OPTIONS).map(([key, option]) => (
                <Option key={key} value={key}>
                  {option}
                </Option>
              ))}
            </TextField>
          ) : null}
          <TextField
            select
            id="viewType"
            name="viewType" 
            value={scheduleView}
            onChange={handleViewTypeChange}
            className={classes.selector}
            variant="outlined"
            SelectProps={{
              native: isIOS,
            }}
          >
            {Object.entries(SCHEDULE_VIEW_TYPES_OPTIONS).map(([key, option]) => (
              <Option key={key} value={key}>
                {option}
              </Option>
            ))}
          </TextField>
        {scheduleView === SCHEDULE_VIEW_TYPES.monthly && (
          <Button
            variant="contained"
            color="secondary"
            onClick={setToday}
            disabled={isToday()}
            disableTouchRipple
          >
            Today
          </Button>
        )}
        {scheduleView !== SCHEDULE_VIEW_TYPES.monthly ? (
          <DayWeekPicker
            selectedDate={selectedDate}
            updateSelectedDate={onDateSelect}
            weekly={scheduleView === SCHEDULE_VIEW_TYPES.weekly}
          />
        ) : (
          <div className={classes.monthPicker}>
            <MonthPicker selectedDate={selectedDate} updateDate={onDateSelect} />
          </div>
        )}
      </div>
    </header>
  );
};

ScheduleV6Header.propTypes = {
  classes: PropTypes.object.isRequired,
  openDrawer: PropTypes.func.isRequired,
  selectedDate: PropTypes.object.isRequired,
  updateSelectedDate: PropTypes.func.isRequired,
  showPublishModal: PropTypes.func.isRequired,
  showUpdateModal: PropTypes.func.isRequired,
  canPublish: PropTypes.bool,
  canUpdate: PropTypes.bool,
  showReconcileModal: PropTypes.func.isRequired,
  updateSetWorklogsControl: PropTypes.func.isRequired,
  hasEditAccess: PropTypes.bool,
};

export default withStyles(styles)(ScheduleV6Header);

